'use client';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { HelpCircle, Globe, Mail, Calendar, Zap, Shield, ChevronDown } from 'lucide-react';

export default function FAQSection() {
  const t = useTranslations();
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: t('faq.items.customDomain.question'), acceptedAnswer: { '@type': 'Answer', text: t('faq.items.customDomain.answer') } },
      { '@type': 'Question', name: t('faq.items.customPages.question'), acceptedAnswer: { '@type': 'Answer', text: t('faq.items.customPages.answer') } },
      { '@type': 'Question', name: t('faq.items.emailNotifications.question'), acceptedAnswer: { '@type': 'Answer', text: t('faq.items.emailNotifications.answer') } },
      { '@type': 'Question', name: t('faq.items.calendarIntegration.question'), acceptedAnswer: { '@type': 'Answer', text: t('faq.items.calendarIntegration.answer') } },
      { '@type': 'Question', name: t('faq.items.noApp.question'), acceptedAnswer: { '@type': 'Answer', text: t('faq.items.noApp.answer') } },
      { '@type': 'Question', name: t('faq.items.trial.question'), acceptedAnswer: { '@type': 'Answer', text: t('faq.items.trial.answer') } },
    ],
  };
  const categories = [
    { icon: Globe, title: t('faq.categories.setup'), questions: ['customDomain', 'customPages', 'trial'], gradient: 'from-blue-500 to-indigo-600' },
    { icon: Calendar, title: t('faq.categories.calendar'), questions: ['calendarIntegration', 'noApp'], gradient: 'from-purple-500 to-pink-600' },
    { icon: Mail, title: t('faq.categories.notifications'), questions: ['emailNotifications'], gradient: 'from-green-500 to-teal-600' },
  ];
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <div className="space-y-16">
        <motion.div className="text-center" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Badge variant="secondary" className="mb-6 border border-blue-200 bg-blue-100 text-blue-700">
            <HelpCircle className="mr-2 h-4 w-4" />{t('faq.badge')}
          </Badge>
          <h1 className="mb-6 text-4xl font-bold text-gray-900 sm:text-5xl">
            {t('faq.title.line1')}{' '}<span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{t('faq.title.line2')}</span>
          </h1>
          <p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-600">{t('faq.subtitle')}</p>
        </motion.div>
        <div className="space-y-12">
          {categories.map((category, idx) => {
            const Icon = category.icon;
            return (
              <motion.div key={category.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}>
                <Card className="overflow-hidden border-2 border-gray-100 shadow-lg">
                  <div className={`bg-gradient-to-r ${category.gradient} p-6`}>
                    <div className="flex items-center gap-3 text-white"><Icon className="h-8 w-8" /><h2 className="text-2xl font-bold">{category.title}</h2></div>
                  </div>
                  <CardContent className="p-6 space-y-4">
                    {category.questions.map((questionKey) => {
                      const qId = `${category.title}-${questionKey}`;
                      const isOpen = openIndex === qId;
                      return (
                        <div key={questionKey} className="rounded-lg border border-gray-200 overflow-hidden">
                          <button onClick={() => setOpenIndex(isOpen ? null : qId)} className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50">
                            <span className="text-lg font-semibold pr-4">{t(`faq.items.${questionKey}.question`)}</span>
                            <ChevronDown className={`h-5 w-5 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                          </button>
                          <AnimatePresence>
                            {isOpen && (
                              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                                <div className="px-6 pb-4 text-base text-gray-600 leading-relaxed">{t(`faq.items.${questionKey}.answer`)}</div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
        <motion.div className="rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white">
          <h3 className="mb-6 text-center text-2xl font-bold">{t('faq.quickFeatures.title')}</h3>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-start gap-3"><Zap className="mt-1 h-6 w-6" /><div><p className="font-semibold">{t('faq.quickFeatures.fast')}</p><p className="text-sm text-blue-100">{t('faq.quickFeatures.fastDesc')}</p></div></div>
            <div className="flex items-start gap-3"><Shield className="mt-1 h-6 w-6" /><div><p className="font-semibold">{t('faq.quickFeatures.secure')}</p><p className="text-sm text-blue-100">{t('faq.quickFeatures.secureDesc')}</p></div></div>
            <div className="flex items-start gap-3"><Globe className="mt-1 h-6 w-6" /><div><p className="font-semibold">{t('faq.quickFeatures.global')}</p><p className="text-sm text-blue-100">{t('faq.quickFeatures.globalDesc')}</p></div></div>
          </div>
        </motion.div>
      </div>
    </>
  );
}
